import { Text, VStack } from '@chakra-ui/react';
import { Button, InputVariant, Radio } from '@pluxee-design-system/core';
import {
  CheckboxControl,
  InputControl,
  RadioGroupControl,
  SubmitButton,
} from '@pluxee-design-system/react-hook-form';
import { useContractDetail } from 'api/contractApi';
import ProductCard from 'common/cards/ProductCard';
import {
  AddressControl,
  CheckboxGroupControl,
  CompanyIdControl,
  DateControl,
  PhoneNumberControl,
  SelectControl,
} from 'common/forms';
import { SectionTitle } from 'common/typography';
import { getChildren } from 'components/Form/ActivityTree/utils';
import { template } from 'components/Onboarding/LocationStep/be.utils';
import {
  findTerminalProviderByTID,
  TERMINAL_DELAY_END,
  TERMINAL_DELAY_START,
} from 'components/Onboarding/utils';
import ProductCheckbox, { ProductCheckboxItem } from 'components/Products/ProductCheckbox';
import { beTerminalsSchema, createSchema } from 'config';
import { format } from 'date-fns';
import { LocationTypeEnum, ProductStatusEnum } from 'generated/models';
import useTranslations from 'i18n';
import { isEqual, pick } from 'lodash';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import gtm, { gtmUtils } from 'trackers/gtm';
import { BelgiumLocationConfig } from 'types/config';
import insertIf from 'utils/insertIf';
import maskString from 'utils/maskString';
import { ValidationError } from 'yup';
import {
  BelgiumOnboardingValues,
  BelgiumValidationContext,
  PaymentOptionEnum,
  PaymentTypeCodeEnum,
  POSValues,
} from '../types';
import OnlineTerminals from './BeOnlineTerminals';
import BePaymentOption, { PaymentOptionItem } from './BePaymentOption';
import Terminals from './BeTerminals';
import { LocationStepType } from './types';

const LocationStep: LocationStepType<BelgiumLocationConfig> = ({
  activities = [],
  contractId,
  isSkippable,
  isOnboarding = true,
  isPaymentEnabled,
  config: rawConfig,
  contractProducts = [],
  steps,
  terminalAcquirers,
  terminalProviders,
  toStep,
}) => {
  const { t } = useTranslations();
  const lastPosChange = useRef<POSValues[]>([]);
  const {
    buttons: configFeature,
    form: config,
    terminals4C: is4cEnabled,
  } = rawConfig as BelgiumLocationConfig;
  const { setValue, watch } = useFormContext<BelgiumOnboardingValues>();
  const { data: contractDetails } = useContractDetail(isSkippable ? undefined : contractId);
  const contract = contractDetails?.contract;

  // watched values
  const [
    valuesLocationName,
    valuesPaymentOptions,
    valuesProducts,
    valuesPOS,
    valuesPOSOnline,
    valuesTerminalTakeover,
    valuesTerminalDelayDate,
    valuesType,
  ] = watch([
    'locationName',
    'paymentOptions',
    'products',
    'POS',
    'POSOnline',
    'terminalTakeover',
    'terminalDelayDate',
    'type',
  ]);

  // codebooks
  const locationTypes = useMemo(
    () =>
      [
        {
          name: t(
            'location_add.sectionTexts.businessInformation.physicalPos',
            'Physical point of sale',
          ),
          key: LocationTypeEnum.Physical,
        },
        {
          name: t('location_add.sectionTexts.businessInformation.onlinePos', 'Webshop'),
          key: LocationTypeEnum.Online,
        },
        {
          name: t('location_add.sectionTexts.businessInformation.marketplacePos', 'Marketplace'),
          key: LocationTypeEnum.Marketplace,
        },
      ].filter((locationType) => isPaymentEnabled!(null, null, locationType.key)),
    [isPaymentEnabled, t],
  );
  const products = useMemo(
    () =>
      contractProducts
        .map<ProductCheckboxItem>(
          ({ allowedPosTypes, payout, product, product: { code, name, nameKey }, status }) => ({
            allowedPosTypes,
            value: code,
            locationType: valuesType || undefined,
            isDisabled: isOnboarding
              ? false
              : status !== undefined && status !== ProductStatusEnum.Active,
            label: t('product_details.products.*.product.name', name, {
              query: { where: 'product_details.products.*.product.code', is: nameKey },
            }),
            payout,
            product,
            status: isOnboarding ? undefined : status,
          }),
        )
        .filter((product) => isPaymentEnabled!(null, [product.value], valuesType)),
    [contractProducts, isOnboarding, isPaymentEnabled, valuesType, t],
  );
  const hasPayment = useCallback(
    (code: PaymentTypeCodeEnum) =>
      isPaymentEnabled!(
        code,
        products.map((p) => p.value),
        valuesType,
      ),
    [isPaymentEnabled, products, valuesType],
  );
  const isPaymentDisabled = useCallback(
    (code: PaymentTypeCodeEnum) => !isPaymentEnabled!(code, valuesProducts, valuesType),
    [isPaymentEnabled, valuesType, valuesProducts],
  );

  const paymentOptions = useMemo<PaymentOptionItem[]>(
    () => [
      ...insertIf(hasPayment(PaymentTypeCodeEnum.Marketplace), {
        value: PaymentOptionEnum.Marketplace,
        label: t('location_add.sectionTexts.paymentOptions.marketplaces.header', 'Marketplaces'),
        description: t(
          'location_add.sectionTexts.paymentOptions.marketplaces.description',
          'Available marketplaces',
        ),
        isDisabled: isPaymentDisabled(PaymentTypeCodeEnum.Marketplace),
      }),
      ...insertIf(hasPayment(PaymentTypeCodeEnum.Payconiq), {
        value: PaymentOptionEnum.Payconiq,
        label: t('location_add.section_texts.paymentOptions.payconiq.header', 'Payconiq'),
        description: t(
          'location_add.section_texts.paymentOptions.payconiq.description',
          'Payconiq',
        ),
        isDisabled: isPaymentDisabled(PaymentTypeCodeEnum.Payconiq),
      }),
      ...insertIf(hasPayment(PaymentTypeCodeEnum.Terminal), {
        value: PaymentOptionEnum.POS,
        label: t(
          'location_add.section_texts.paymentOptions.paymentTerminal.header',
          'Payment terminal',
        ),
        description: t(
          'location_add.section_texts.paymentOptions.paymentTerminal.description',
          'Payment terminal',
        ),
        isDisabled: isPaymentDisabled(PaymentTypeCodeEnum.Terminal),
        content: (
          <Terminals
            config={config}
            is4cEnabled={is4cEnabled}
            isOnboarding={isOnboarding}
            terminalAcquirers={terminalAcquirers!}
            terminalProviders={terminalProviders!}
          />
        ),
      }),
      ...insertIf(hasPayment(PaymentTypeCodeEnum.PSP), {
        value: PaymentOptionEnum.PSP,
        label: t(
          'location_add.sectionTexts.paymentOptions.onlinePaymentTerminal.header',
          'Payment service provider',
        ),
        description: t(
          'location_add.sectionTexts.paymentOptions.onlinePaymentTerminal.description',
          'Payment service provider',
        ),
        isDisabled: isPaymentDisabled(PaymentTypeCodeEnum.PSP),
        content: (
          <OnlineTerminals
            config={config}
            is4cEnabled={is4cEnabled}
            isOnboarding={isOnboarding}
            disabled={isPaymentDisabled(PaymentTypeCodeEnum.PSP)}
            terminalAcquirers={terminalAcquirers!}
          />
        ),
      }),
    ],
    [
      config,
      hasPayment,
      is4cEnabled,
      isOnboarding,
      isPaymentDisabled,
      terminalAcquirers,
      terminalProviders,
      t,
    ],
  );
  const activitiesNoParent = useMemo(() => getChildren(activities), [activities]);
  const prefilledCompanyInfo = useMemo(() => {
    const company = contract?.company;
    const address = company?.addrHeadquarters;
    return {
      ...(config.locationName.visible ? { locationName: company?.nameCommercial || '' } : {}),
      ...(config.streetName.visible ? { streetName: address?.street || '' } : {}),
      ...(config.houseNumber.visible ? { houseNumber: address?.houseNumber || '' } : {}),
      ...(config.city.visible ? { city: address?.city || '' } : {}),
      ...(config.box.visible ? { box: address?.box || '' } : {}),
      ...(config.postalCode.visible
        ? {
            postalCode: maskString(address?.zip || '', config.postalCode.format),
          }
        : {}),
      ...(config.accountNumber.visible
        ? { accountNumber: contract?.bankAccount?.accountIban || '' }
        : {}),
      ...(config.typeOfActivity.visible &&
      activitiesNoParent.some((a) => a.value === contract?.services?.[0])
        ? { typeOfActivity: contract?.services?.[0] || '' }
        : {}),
    };
  }, [activitiesNoParent, config, contract]);

  const handleSameCompanyInfo = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        Object.entries(prefilledCompanyInfo).forEach(([key, info]) =>
          setValue(key as keyof BelgiumOnboardingValues, info, {
            shouldTouch: true,
            shouldDirty: true,
            shouldValidate: true,
          }),
        );
        setValue('sameCompanyInfo', true);
      } else {
        setValue('sameCompanyInfo', false);
      }
    },
    [prefilledCompanyInfo, setValue],
  );
  const trackSubmit = useCallback(() => {
    const findPaymentOption = (o: PaymentOptionEnum) => {
      if (valuesPaymentOptions.includes(o))
        return paymentOptions.find((po) => po.value === o && !po.disabled);
      return undefined;
    };

    const marketplace = findPaymentOption(PaymentOptionEnum.Marketplace);
    const payconiq = findPaymentOption(PaymentOptionEnum.Payconiq);
    const pos = findPaymentOption(PaymentOptionEnum.POS);
    const psp = findPaymentOption(PaymentOptionEnum.PSP);

    gtm.trackClickLocationConfirm({
      event_location_name: valuesLocationName,
      event_activity: gtmUtils.getSelectedOptions('typeOfActivity'),
      event_accepted_product: valuesProducts.reduce((result, curr) => {
        const productName = products.find((p) => p.value === curr)?.label;
        if (productName) result.push(productName);
        return result;
      }, [] as string[]),
      event_payment_option: [
        ...insertIf(marketplace, { name: marketplace?.label }),
        ...insertIf(payconiq, { name: payconiq?.label }),
        ...insertIf(pos, {
          name: pos?.label,
          options: valuesPOS.map((pos) => [pos.terminalId, pos.terminalProvider]),
        }),
        ...insertIf(psp, {
          name: psp?.label,
          options: valuesPOSOnline.map((pos) => [pos.terminalId, pos.terminalProvider]),
        }),
      ],
      event_terminal_created_number: pos ? valuesPOS.length : undefined,
      event_activation_date:
        valuesType === LocationTypeEnum.Physical && valuesTerminalDelayDate
          ? format(new Date(valuesTerminalDelayDate), 'yyyy-MM-dd')
          : undefined,
    });
  }, [
    paymentOptions,
    products,
    valuesLocationName,
    valuesPaymentOptions,
    valuesProducts,
    valuesPOS,
    valuesTerminalDelayDate,
    valuesPOSOnline,
    valuesType,
  ]);

  useEffect(() => {
    const values = watch();
    const isCompanyInfoPrefilled = isEqual(
      pick(values, Object.keys(prefilledCompanyInfo)),
      prefilledCompanyInfo,
    );
    if (values.sameCompanyInfo && !isCompanyInfoPrefilled) {
      setValue('sameCompanyInfo', false);
    } else if (!values.sameCompanyInfo && isCompanyInfoPrefilled) {
      // setValue('sameCompanyInfo', true, false); // to auto tick the checkbox
    }
  }, [prefilledCompanyInfo, setValue, watch]);

  useEffect(() => {
    if (!isEqual(valuesPOS, lastPosChange.current)) {
      const hasOption = valuesPaymentOptions.includes(PaymentOptionEnum.POS);
      if (!hasOption && valuesPOS.length > 0 && valuesPOS.length >= lastPosChange.current.length) {
        setValue('paymentOptions', [...valuesPaymentOptions, PaymentOptionEnum.POS]);
      } else if (hasOption && valuesPOS.length === 0) {
        setValue(
          'paymentOptions',
          valuesPaymentOptions.filter((o) => o !== PaymentOptionEnum.POS),
        );
      }
    }
    lastPosChange.current = valuesPOS;
  }, [valuesPaymentOptions, valuesPOS, setValue]);

  useEffect(() => {
    if (isOnboarding && valuesType) {
      gtm.trackClickLocationSelectType(valuesType);
    }
  }, [isOnboarding, valuesType]);

  return (
    <>
      <VStack align="stretch" spacing={4}>
        <SectionTitle
          title={t('location_add.sectionTexts.businessInformation.header', 'Business information')}
          description={t('location_add.sectionTexts.businessInformation.subheader', '')}
        />
        <RadioGroupControl stackProps={{ direction: { base: 'column', md: 'row' } }} name="type">
          {locationTypes.map((locationType) => (
            <ProductCard
              key={locationType.key}
              variant={valuesType === locationType.key ? 'selected' : InputVariant.FILLED}
            >
              <Radio value={locationType.key}>{locationType.name}</Radio>
            </ProductCard>
          ))}
        </RadioGroupControl>
        {valuesType !== '' && (
          <>
            {!isSkippable && isOnboarding && (
              <CheckboxControl
                name="sameCompanyInfo"
                id="LocationStep_sameCompanyInfo"
                onChange={handleSameCompanyInfo}
              >
                {valuesType === LocationTypeEnum.Physical
                  ? t(
                      'location_add.sectionTexts.businessInformation.useCompanyInfoPhysicalPos',
                      'Use the same information from the company (Address, IBAN, ...)',
                    )
                  : t(
                      'location_add.sectionTexts.businessInformation.useCompanyInfoOnlinePos',
                      'Use the same IBAN from the company',
                    )}
              </CheckboxControl>
            )}
            {config.locationName.visible && (
              <InputControl
                type="text"
                mandatory={config.locationName.mandatory}
                name="locationName"
                id="LocationStep_locationName"
                placeholder={t('location_add.form.locationName.label', 'Point of sale name')}
                title={t('location_add.form.locationName.label', 'Point of sale name')}
              />
            )}
            {valuesType === LocationTypeEnum.Physical && (
              <AddressControl
                config={config}
                idPrefix="LocationStep"
                tPrefix="location_add.form"
                withSuggestion={false}
              />
            )}
            {(valuesType === LocationTypeEnum.Physical || valuesType === LocationTypeEnum.Online) &&
              config.website.visible && (
                <InputControl
                  type="text"
                  mandatory={valuesType === LocationTypeEnum.Online && config.website.mandatory}
                  name="website"
                  id="LocationStep_website"
                  placeholder={t('location_add.form.website.label', 'Website')}
                  title={t('location_add.form.website.label', 'Website')}
                />
              )}
            {(valuesType === LocationTypeEnum.Online ||
              valuesType === LocationTypeEnum.Marketplace) &&
              config.email.visible && (
                <InputControl
                  type="email"
                  mandatory={config.email.mandatory}
                  name="email"
                  id="LocationStep_email"
                  placeholder={t('location_add.form.email.label', 'Email')}
                  title={t('location_add.form.email.label', 'Email')}
                />
              )}
            {config.phoneNumber.visible && (
              <PhoneNumberControl
                mandatory={config.phoneNumber.mandatory}
                name="phoneNumber"
                id="LocationStep_phoneNumber"
                title={t('location_add.form.phoneNumber.label', 'Primary phone')}
              />
            )}
            {valuesType === LocationTypeEnum.Physical && configFeature.terminalTakeoverVisible && (
              <>
                <CheckboxControl name="terminalTakeover" id="LocationStep_terminalTakeover">
                  {t(
                    'location_add.sectionTexts.businessInformation.terminalTakeover',
                    'Are you taking over a store?',
                  )}
                </CheckboxControl>
                {valuesTerminalTakeover && (
                  <>
                    {config.terminalDelayDate.visible && (
                      <DateControl
                        mandatory={config.terminalDelayDate.mandatory}
                        name="terminalDelayDate"
                        id="LocationStep_terminalDelayDate"
                        placeholder={t(
                          'location_add.form.terminalDelayDate.placeholder',
                          'Delay date',
                        )}
                        title={t(
                          'location_add.form.terminalDelayDate.label',
                          "If you're taking over a store, please select a date at which we should activate your terminals",
                        )}
                        minDate={TERMINAL_DELAY_START}
                        maxDate={TERMINAL_DELAY_END}
                      />
                    )}
                    {config.terminalCompanyId.visible && (
                      <CompanyIdControl
                        mandatory={config.terminalCompanyId.mandatory}
                        name="terminalCompanyId"
                        id="LocationStep_terminalCompanyId"
                        placeholder={t(
                          'location_add.form.terminalCompanyId.placeholder',
                          '1234.567.890',
                        )}
                        title={t(
                          'location_add.form.terminalCompanyId.label',
                          `Provide company number of the company from which you're taking over the terminal`,
                        )}
                        format={config.terminalCompanyId.format}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </VStack>
      {valuesType !== '' && (
        <>
          {config.typeOfActivity.visible && (
            <VStack align="stretch" spacing={4}>
              <SectionTitle
                title={t(
                  'location_add.sectionTexts.furtherInformation.header',
                  'Further information',
                )}
              />
              <SelectControl
                mandatory={config.typeOfActivity.mandatory}
                name="typeOfActivity"
                id="LocationStep_typeOfActivity"
                placeholder={t('location_add.form.typeOfActivity.label', 'Activity')}
                title={t('location_add.form.typeOfActivity.label', 'Activity')}
                notFoundText={t('global_texts.labels.noResults', 'No results matching a query')}
                options={activitiesNoParent}
              />
            </VStack>
          )}
          {config.products.visible && (
            <VStack align="stretch" spacing={4}>
              <SectionTitle
                title={t('location_add.sectionTexts.acceptedProducts.header', 'Accepted products')}
                description={t('location_add.sectionTexts.acceptedProducts.subheader', '')}
              />
              <CheckboxGroupControl
                mandatory={config.products.mandatory}
                name="products"
                id="LocationStep_products"
                options={products}
                renderComponent={ProductCheckbox}
              />
            </VStack>
          )}
          <VStack align="stretch" spacing={4}>
            <SectionTitle
              title={t('location_add.sectionTexts.paymentOptions.header', 'Payment options')}
              description={t('location_add.sectionTexts.paymentOptions.subheader', '')}
            />
            <CheckboxGroupControl
              mandatory={config.products.mandatory}
              name="paymentOptions"
              id="LocationStep_paymentOptions"
              options={paymentOptions}
              renderComponent={BePaymentOption}
              // title={t(
              //   'location_add.sectionTexts.paymentOptions.subheader',
              //   'Select one or multiple payment options for your location',
              // )}
            />
          </VStack>
          {config.accountNumber.visible && (
            <VStack align="stretch" spacing={4}>
              <SectionTitle
                title={t(
                  'location_add.sectionTexts.bankingInformation.header',
                  'Banking information',
                )}
              />
              <InputControl
                type="text"
                mandatory={config.accountNumber.mandatory}
                name="accountNumber"
                id="LocationStep_accountNumber"
                placeholder={t('payment_info.form.accountNumber.label', 'Account number')}
                title={t('payment_info.form.accountNumber.label', 'Account number')}
                leftElement={
                  config.accountNumber.format && (
                    <Text variant="body.largeDemibold" color="semantic.text.primary">
                      {config.accountNumber.format}
                    </Text>
                  )
                }
              />
            </VStack>
          )}
          <VStack align="stretch" spacing={4}>
            <SubmitButton
              variant="primaryFilled"
              id="LocationStep__doneButton"
              onClick={isOnboarding ? trackSubmit : undefined}
              width="100%"
            >
              {t('location_add.buttons.save_location', 'Save')}
            </SubmitButton>
            {isSkippable && (
              <Button
                variant="primaryOutlined"
                id="LocationStep__skipButton"
                type="reset"
                onClick={() => toStep(steps.length)}
                width="100%"
                background="semantic.surface.1"
              >
                {t('location_add.buttons.skip', 'Skip')}
              </Button>
            )}
          </VStack>
        </>
      )}
    </>
  );
};

LocationStep.initial = () => ({
  type: '',
  sameCompanyInfo: false,

  // common
  locationName: '',
  phoneNumber: '',
  typeOfActivity: '',
  products: [],
  accountNumber: '',
  website: '',

  // physical
  streetName: '',
  houseNumber: '',
  box: '',
  city: '',
  postalCode: '',
  POS: [template('0')],
  paymentOptions: Object.values(PaymentOptionEnum), // to check all options
  terminalTakeover: false,
  terminalCompanyId: '',
  terminalDelayDate: null,
  terminalAcquirer: '',
  terminalMerchantId: '',

  // webshop
  email: '',
  terminalAcquirerOnline: '',
  terminalMerchantIdOnline: '',
  POSOnline: [template('0')],
});

LocationStep.validate =
  ({ config, isPaymentEnabled, terminalAcquirers, terminalProviders }) =>
  async (values: BelgiumOnboardingValues) => {
    let errors: ValidationError[] = [];

    try {
      const isPaymentOptionEnabled = (code: PaymentTypeCodeEnum) =>
        isPaymentEnabled!(code, values.products, values.type);
      const isProductEnabled = (productCode: string) =>
        isPaymentEnabled!(null, [productCode], values.type);
      const isTIDValid = (tid: string) =>
        Boolean(findTerminalProviderByTID(tid, terminalProviders!.tids!));

      const schema = createSchema({
        locationName: config.form.locationName,
        phoneNumber: config.form.phoneNumber,
        typeOfActivity: config.form.typeOfActivity,
        products: config.form.products,
        accountNumber: config.form.accountNumber,
        website: config.form.website,
        paymentOptions: config.form.paymentOptions,

        // physical
        streetName: config.form.streetName,
        houseNumber: config.form.houseNumber,
        box: config.form.box,
        city: config.form.city,
        postalCode: config.form.postalCode,
        // physical - terminal takeover
        terminalCompanyId: config.form.terminalCompanyId,
        terminalDelayDate: config.form.terminalDelayDate,

        // webshop
        email: config.form.email,
      })
        .concat(beTerminalsSchema(''))
        .concat(beTerminalsSchema('Online'));
      await schema.validate(values, {
        context: {
          isPaymentOptionEnabled,
          isProductEnabled,
          isTIDValid,
          terminalAcquirers,
          terminalAcquirer: values.terminalAcquirer,
          terminalAcquirerOnline: values.terminalAcquirerOnline,
          terminalIds: {},
        } as BelgiumValidationContext,
        abortEarly: false,
      });
    } catch (error) {
      // abortEarly = false, must access inner
      errors = error.inner;
    }

    return errors;
  };

export default LocationStep;
